import React, { useState } from "react";
import { Users, BookOpen, Bot, Cpu, Boxes, Radio, Check } from "lucide-react";
import { track } from "../../lib/analytics";

const CASES = [
  {
    id: "personnel",
    icon: Users,
    name: "Personnel AI",
    summary: "Govern employee use of generative AI and copilots.",
    points: [
      "Sensitive information disclosure",
      "Restricted document submission",
      "Inappropriate AI usage",
      "Policy violations",
    ],
    controlNote: "Apply organisation-defined policy to what personnel can send to, and receive from, AI tools.",
  },
  {
    id: "knowledge",
    icon: BookOpen,
    name: "Internal knowledge assistants",
    summary: "Apply access and information-handling policies around AI assistants accessing organisational knowledge.",
    points: [
      "Enforce role-based access to sources",
      "Constrain what may be surfaced",
      "Handle sensitive content by policy",
      "Record what was retrieved and why",
    ],
    controlNote: "Independent handling policy around assistants that read internal knowledge.",
  },
  {
    id: "copilots",
    icon: Bot,
    name: "AI copilots",
    summary: "Monitor inputs and outputs and route designated high-risk interactions to human review.",
    points: [
      "Inspect prompts and generated outputs",
      "Warn, redact or block by policy",
      "Escalate material cases to reviewers",
      "Evidence each intervention",
    ],
    controlNote: "Runtime inspection of copilot inputs and outputs with human escalation.",
  },
  {
    id: "agents",
    icon: Cpu,
    name: "AI agents",
    summary: "Apply policies before agents perform designated actions or access controlled tools.",
    points: [
      "Evaluate proposed actions against policy",
      "Hold actions for human authorisation",
      "Constrain tool and scope access",
      "Record approve / reject decisions",
    ],
    controlNote: "Action-level governance before an agent executes or accesses a controlled tool.",
  },
  {
    id: "supplier",
    icon: Boxes,
    name: "Supplier & third-party AI",
    summary: "Create an independent control layer around approved external AI systems rather than relying exclusively on provider safeguards.",
    points: [
      "Independent of the provider's controls",
      "Consistent policy across vendors",
      "Inspect inbound and outbound content",
      "Evidence handling for assurance",
    ],
    controlNote: "An independent layer around external AI, not a substitute for supplier due diligence.",
  },
  {
    id: "command",
    icon: Radio,
    name: "Command / operational support",
    summary: "Support governance around AI-assisted workflows where human accountability, evidence and policy enforcement are required.",
    points: [
      "Human accountability at defined points",
      "Policy enforcement on AI-assisted steps",
      "Reviewable evidence of decisions",
    ],
    controlNote: "Discussed at a high level only. norse3 is not approved for targeting, weapons release, autonomous lethal action or classified operational decision-making.",
    guarded: true,
  },
];

export default function UseCaseSelector() {
  const [active, setActive] = useState(CASES[0].id);
  const cur = CASES.find((c) => c.id === active);
  const Icon = cur.icon;

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4" data-testid="defence-usecases">
      <div className="flex flex-col gap-1.5">
        {CASES.map((c) => {
          const CIcon = c.icon;
          const on = c.id === active;
          return (
            <button
              key={c.id}
              onClick={() => { setActive(c.id); track("defence_usecase_selected", { usecase: c.id }); }}
              data-testid={`defence-usecase-${c.id}`}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                on ? "border-primary/40 bg-primary/[0.07]" : "border-white/10 bg-navy-900/60 hover:border-white/20"
              }`}
            >
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${on ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-muted-foreground"}`}>
                <CIcon className="h-5 w-5" />
              </span>
              <span className={`text-sm font-medium ${on ? "text-foreground" : "text-muted-foreground"}`}>{c.name}</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
          <h3 className="text-xl font-bold text-foreground">{cur.name}</h3>
        </div>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">{cur.summary}</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
          {cur.points.map((p) => (
            <div key={p} className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span className="text-sm text-foreground/90">{p}</span>
            </div>
          ))}
        </div>
        <p className={`mt-5 rounded-lg border px-4 py-3 text-sm ${cur.guarded ? "border-signal-amber/30 bg-signal-amber/[0.05] text-signal-amber/90" : "border-primary/25 bg-primary/[0.05] text-muted-foreground"}`}>
          {cur.controlNote}
        </p>
      </div>
    </div>
  );
}
